import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  ConversationContextAttachment,
  ConversationContextMessage,
  ConversationContextSite,
} from 'src/graphql/entities/conversation/conversation-context.operations';
import {
  DocumentBuilder,
  type ExportDocument,
  italic,
  link,
  raw,
  renderDocumentAsMarkdown,
  spans,
  text,
} from 'src/pages/Conversations/document';
import {
  decodeHtmlEntities,
  extractImageTagsFromHtml,
  getDisplayedTextFromHtml,
} from 'src/utils/text-parsing';
import {
  canIncludeInternalContent,
  createConversationMarkdownPolicy,
} from './conversation-markdown-policy';
import { ConvertConversationContextToMarkdownInput } from './conversation-markdown.types';
import { isUrlLike } from './markdown-escape';

dayjs.extend(utc);
dayjs.extend(timezone);

const READABLE_TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const DEFAULT_IMAGE_ALT = 'Inline image';

export interface ConversationDocumentBuildResult {
  document: ExportDocument;
  includedMessageCount: number;
}

const isDefined = (value?: string | null): value is string => {
  return typeof value === 'string' && value.trim().length > 0;
};

function normalizeRichTextField(value?: string | null): string {
  if (!isDefined(value)) {
    return '';
  }

  const decodedValue = decodeHtmlEntities(value);
  const normalizedText = getDisplayedTextFromHtml(decodedValue).trim();
  const result = normalizedText.length > 0 ? normalizedText : decodedValue.trim();

  return result.replace(/\r?\n/g, ' ');
}

const formatReadableMessageTimestamp = ({
  createdAt,
  timezoneName,
}: {
  createdAt?: string | null;
  timezoneName?: string | null;
}): string => {
  if (!isDefined(createdAt)) {
    return 'Unknown time';
  }

  if (isDefined(timezoneName)) {
    return dayjs(createdAt).tz(timezoneName).format(READABLE_TIMESTAMP_FORMAT);
  }

  return dayjs(createdAt).format(READABLE_TIMESTAMP_FORMAT);
};

const sortByCreatedAtAsc = (
  messages: ConversationContextMessage[],
): ConversationContextMessage[] => {
  return [...messages].sort((first, second) => {
    const firstTs = first.createdAt
      ? dayjs(first.createdAt).valueOf()
      : Number.MAX_SAFE_INTEGER;
    const secondTs = second.createdAt
      ? dayjs(second.createdAt).valueOf()
      : Number.MAX_SAFE_INTEGER;

    return firstTs - secondTs;
  });
};

const addSummaryMetadataItems = (
  doc: DocumentBuilder,
  input: ConvertConversationContextToMarkdownInput,
): void => {
  if (isDefined(input.context.status)) {
    doc.listItem(text(`Status: ${input.context.status}`));
  } else {
    doc.listItem(text('Status: Unknown'));
  }

  if (isDefined(input.context.type)) {
    doc.listItem(text(`Type: ${input.context.type}`));
  }

  if (isDefined(input.context.urgency)) {
    doc.listItem(text(`Urgency: ${input.context.urgency}`));
  }

  const labels = (input.context.labels ?? []).filter(isDefined);
  if (labels.length > 0) {
    doc.listItem(text(`Labels: ${labels.join(', ')}`));
  }
};

const addPartnerSection = (
  doc: DocumentBuilder,
  input: ConvertConversationContextToMarkdownInput,
): void => {
  if (!isDefined(input.context.partner?.businessName)) {
    return;
  }

  doc.heading(2, text('Partner'));
  doc.listItem(text(`Business Name: ${input.context.partner.businessName}`));
};

const addParticipantsSection = (
  doc: DocumentBuilder,
  {
    input,
    includeInternalContent,
  }: {
    input: ConvertConversationContextToMarkdownInput;
    includeInternalContent: boolean;
  },
): void => {
  doc.heading(2, text('Participants'));

  const agents = (input.context.participants?.agents ?? []).filter(isDefined);
  const clients = (input.context.participants?.clients ?? []).filter(Boolean);

  doc.heading(3, text('Agents'));
  if (agents.length === 0) {
    doc.listItem(italic('No agents listed'));
  } else {
    agents.forEach((agent) => {
      doc.listItem(text(agent));
    });
  }

  doc.heading(3, text('Clients'));
  if (clients.length === 0) {
    doc.listItem(italic('No clients listed'));
    return;
  }

  clients.forEach((client) => {
    if (!isDefined(client.name)) {
      return;
    }

    doc.listItem(text(client.name));

    if (!input.policy?.includeVisibleClientDetails) {
      return;
    }

    if (isDefined(client.primaryPhoneNumber)) {
      doc.listItem(text(`Primary Phone Number: ${client.primaryPhoneNumber}`), 1);
    }

    if (isDefined(client.primaryEmail)) {
      doc.listItem(text(`Primary Email: ${client.primaryEmail}`), 1);
    }

    if (includeInternalContent && isDefined(client.internalNotes)) {
      doc.listItem(
        text(`Internal Notes (Internal): ${normalizeRichTextField(client.internalNotes)}`),
        1,
      );
    }
  });
};

const addAttachmentElements = (
  doc: DocumentBuilder,
  attachment: ConversationContextAttachment,
): void => {
  const type = isDefined(attachment.type) ? attachment.type : 'attachment';
  const title = isDefined(attachment.title) ? attachment.title : type;
  const content = attachment.content;

  if (isDefined(type) && type.toLowerCase().includes('transcription') && isDefined(content)) {
    doc.listItem(text(`${title}:`));
    doc.blockquote(text(content));
    return;
  }

  if (isDefined(content) && isUrlLike(content)) {
    doc.listItem(link(title, content));
    return;
  }

  if (isDefined(content)) {
    doc.listItem(text(`${title} (${type}): ${content}`));
    return;
  }

  doc.listItem(text(`${title} (${type})`));
};

const normalizeMessageContent = ({
  messageText,
  rawBody,
}: {
  messageText?: string | null;
  rawBody?: string | null;
}): { images: { src: string; alt: string }[]; plainText: string | null } => {
  const sourceBody = isDefined(rawBody) ? rawBody : messageText;

  if (!isDefined(sourceBody)) {
    return { images: [], plainText: null };
  }

  const normalizedHtml = decodeHtmlEntities(sourceBody);
  const images = extractImageTagsFromHtml(normalizedHtml)
    .filter((image) => isUrlLike(image.src))
    .map((image) => ({
      src: image.src,
      alt: isDefined(image.alt) ? image.alt : DEFAULT_IMAGE_ALT,
    }));
  const extracted = getDisplayedTextFromHtml(normalizedHtml).trim();
  const plainText = extracted.length > 0 ? extracted : null;

  return { images, plainText };
};

const addMessageElements = (
  doc: DocumentBuilder,
  message: ConversationContextMessage,
  timezoneName?: string | null,
): void => {
  const headerTimestamp = formatReadableMessageTimestamp({
    createdAt: message.createdAt,
    timezoneName,
  });
  const author = isDefined(message.author) ? message.author : 'Unknown Author';
  const role = isDefined(message.role) ? message.role : 'Unknown Role';
  doc.heading(
    3,
    message.isInternal
      ? spans(
          text(`${headerTimestamp} - `),
          text(author),
          text(` (${role})`),
          raw(' [Internal]'),
        )
      : spans(text(`${headerTimestamp} - `), text(author), text(` (${role})`)),
  );

  const { images, plainText } = normalizeMessageContent({
    messageText: message.text,
    rawBody: message.rawBody,
  });

  if (images.length === 0 && plainText === null) {
    const fallbackText = isDefined(message.text) ? message.text : null;
    doc.paragraph(fallbackText !== null ? raw(fallbackText) : italic('No message text'));
  } else {
    images.forEach((image) => {
      doc.image(image.src, image.alt);
    });

    if (plainText !== null) {
      doc.paragraph(raw(plainText));
    }
  }

  const attachments = (message.attachments ?? []).filter(Boolean);
  if (attachments.length > 0) {
    doc.paragraph(text('Attachments:'));
    attachments.forEach((attachment) => {
      addAttachmentElements(doc, attachment);
    });
  }
};

const addMessagesSection = (
  doc: DocumentBuilder,
  {
    input,
    includeInternalContent,
    timezoneName,
  }: {
    input: ConvertConversationContextToMarkdownInput;
    includeInternalContent: boolean;
    timezoneName?: string | null;
  },
): number => {
  doc.heading(2, text('Messages'));

  const sortedMessages = sortByCreatedAtAsc((input.context.messages ?? []).filter(Boolean));
  const includedMessages = sortedMessages.filter((message) => {
    if (!includeInternalContent && message.isInternal) {
      return false;
    }

    return true;
  });

  if (includedMessages.length === 0) {
    doc.paragraph(italic('No messages available'));
    return 0;
  }

  includedMessages.forEach((message) => {
    addMessageElements(doc, message, timezoneName);
  });

  return includedMessages.length;
};

const addAssociatedSitesSection = (
  doc: DocumentBuilder,
  {
    sites,
    includeInternalContent,
  }: {
    sites: ConversationContextSite[];
    includeInternalContent: boolean;
  },
): void => {
  if (sites.length === 0) {
    return;
  }

  doc.startSection('associated-sites');
  doc.heading(2, text('Associated Sites'));

  sites.forEach((site, index) => {
    doc.heading(3, text(`Site ${index + 1}`));

    if (isDefined(site.address)) {
      doc.listItem(text(`Address: ${site.address}`));
    }

    if (isDefined(site.serviceLevel)) {
      doc.listItem(text(`Service Level: ${site.serviceLevel}`));
    }

    if (isDefined(site.cppExpDate)) {
      doc.listItem(text(`CPP Expiration Date: ${site.cppExpDate}`));
    }

    if (includeInternalContent && isDefined(site.internalSiteNotes)) {
      doc.listItem(
        text(
          `Internal Site Notes (Internal): ${normalizeRichTextField(site.internalSiteNotes)}`,
        ),
      );
    }

    if (includeInternalContent && isDefined(site.internalMembershipNotes)) {
      doc.listItem(
        text(
          `Internal Membership Notes (Internal): ${normalizeRichTextField(site.internalMembershipNotes)}`,
        ),
      );
    }

    if (isDefined(site.activationFlowUrl) && isUrlLike(site.activationFlowUrl)) {
      doc.listItem(
        spans(text('Activation Flow: '), link('Open Link', site.activationFlowUrl)),
      );
    }

    const associatedConversations = (site.associatedConversations ?? []).filter(Boolean);
    if (associatedConversations.length > 0) {
      doc.listItem(text('Associated Conversations:'));
      associatedConversations.forEach((conversation) => {
        const friendlyId = isDefined(conversation.friendlyId)
          ? conversation.friendlyId
          : 'Unknown';
        const subject = isDefined(conversation.subject) ? conversation.subject : 'No subject';
        const status = isDefined(conversation.status) ? conversation.status : 'Unknown status';

        doc.listItem(spans(raw(`[${friendlyId}] `), text(`${subject} (${status})`)), 1);
      });
    }
  });
};

const addMetadataSection = (
  doc: DocumentBuilder,
  {
    input,
    messageCount,
    participantCount,
    associatedSiteCount,
    exportedAt,
  }: {
    input: ConvertConversationContextToMarkdownInput;
    messageCount: number;
    participantCount: number;
    associatedSiteCount: number;
    exportedAt: string;
  },
): void => {
  doc.startSection('metadata');
  doc.heading(2, text('Metadata'));

  addSummaryMetadataItems(doc, input);

  doc.listItem(text(`Conversation UUID: ${input.conversation.conversationId}`));
  doc.listItem(text(`Friendly ID: ${input.conversation.friendlyId}`));

  if (isDefined(input.conversationUrl)) {
    doc.listItem(text(`URL: ${input.conversationUrl}`));
  }

  if (input.conversation.partnerId !== undefined && input.conversation.partnerId !== null) {
    doc.listItem(text(`Partner ID: ${input.conversation.partnerId}`));
  }

  if (isDefined(input.conversation.createdAt)) {
    doc.listItem(text(`Conversation Created At: ${input.conversation.createdAt}`));
  }

  if (isDefined(input.conversation.conversationStatusUpdatedAt)) {
    doc.listItem(
      text(
        `Conversation Status Updated At: ${input.conversation.conversationStatusUpdatedAt}`,
      ),
    );
  }

  if (isDefined(input.context.requester)) {
    doc.listItem(text(`Requester: ${input.context.requester}`));
  }

  doc.listItem(text(`Exported At: ${exportedAt}`));
  doc.listItem(text(`Message Count: ${messageCount}`));
  doc.listItem(text(`Participant Count: ${participantCount}`));
  doc.listItem(text(`Associated Site Count: ${associatedSiteCount}`));
};

export const buildConversationDocumentWithStats = (
  input: ConvertConversationContextToMarkdownInput,
): ConversationDocumentBuildResult => {
  const policy = createConversationMarkdownPolicy(input.policy);
  const includeInternalContent = canIncludeInternalContent(policy);
  const partnerTimeZone: string | null =
    typeof input.context.partner?.timeZone === 'string'
      ? input.context.partner.timeZone
      : null;
  const conversationTimezone = partnerTimeZone ?? 'UTC';
  const timezoneName: string | undefined =
    input.viewerTimezoneName ??
    (policy.timezoneStrategy === 'conversation_tz_or_utc' ? conversationTimezone : undefined);
  const title =
    input.context.subject ??
    input.conversation.subject ??
    `Conversation ${input.conversation.friendlyId}`;

  const doc = new DocumentBuilder(title);

  addPartnerSection(doc, input);
  doc.hr();
  addParticipantsSection(doc, {
    input: { ...input, policy },
    includeInternalContent,
  });
  doc.hr();
  const includedMessageCount = addMessagesSection(doc, {
    input,
    includeInternalContent,
    timezoneName,
  });

  const sites = (input.context.associatedSites ?? []).filter(Boolean);
  addAssociatedSitesSection(doc, { sites, includeInternalContent });

  const agents = (input.context.participants?.agents ?? []).filter(isDefined);
  const clients = (input.context.participants?.clients ?? []).filter(Boolean);
  addMetadataSection(doc, {
    input,
    messageCount: includedMessageCount,
    participantCount: agents.length + clients.length,
    associatedSiteCount: sites.length,
    exportedAt: input.exportedAt ?? new Date().toISOString(),
  });

  return {
    document: doc.build(),
    includedMessageCount,
  };
};

export const buildConversationDocument = (
  input: ConvertConversationContextToMarkdownInput,
): ExportDocument => {
  return buildConversationDocumentWithStats(input).document;
};

export const convertConversationContextToMarkdown = (
  input: ConvertConversationContextToMarkdownInput,
): string => {
  return renderDocumentAsMarkdown(buildConversationDocument(input));
};
